import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './clientSettings.css'; // Styles specific to the client settings page

function ClientSettings() {
    const [deletionBusy, setDeletionBusy] = useState(false);
    const [deletionMsg, setDeletionMsg] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [preferencesError, setPreferencesError] = useState('');
    const [preferencesSuccess, setPreferencesSuccess] = useState('');

    const [phoneNumber, setPhoneNumber] = useState('');
    const [isWhatsappNotifications, setIsWhatsappNotifications] = useState(false);

    useEffect(() => {
        const fetchClientSettings = async () => {
            try {
                const res = await axios.get('/api/client-requests/settings/me', { withCredentials: true });
                setPhoneNumber(res.data.phone_number || '');
                setIsWhatsappNotifications(!!res.data.is_whatsapp_notifications);
            } catch (err) {
                console.error('Failed to load client settings', err);
            }
        };
        fetchClientSettings();
    }, []);


    // Handle password change
    const handleChangePassword = async (e) => {
        e.preventDefault();

        // Check if the new password and confirm new password match
        if (newPassword !== confirmNewPassword) {
            setPasswordError('New password and confirm new password do not match');
            setPasswordSuccess('');
            return;
        }

        try {
            await axios.post(
                '/api/users/change_password',
                {
                    currentPassword,
                    newPassword,
                },
                { withCredentials: true }
            );
            setPasswordSuccess('Password changed successfully');
            setPasswordError('');
            setCurrentPassword(''); // Clear input fields
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (error) {
            setPasswordError('Failed to change password. Please try again.');
            setPasswordSuccess('');
        }
    };

    // Handle phone / WhatsApp notification settings
    const handleSaveContactSettings = async (e) => {
        e.preventDefault();

        try {
            await axios.put(
                '/api/client-requests/settings/me',
                {
                    phone_number: phoneNumber,
                    is_whatsapp_notifications: isWhatsappNotifications,
                },
                { withCredentials: true }
            );

            setPreferencesSuccess('Contact settings updated successfully');
            setPreferencesError('');
        } catch (error) {
            console.error('Failed to update contact settings', error);

            const serverMessage = error.response?.data?.error;

            if (error.response?.status === 400 && serverMessage) {
                // e.g. "This phone number is already in use by another account."
                setPreferencesError(serverMessage);
            } else {
                setPreferencesError('Failed to update contact settings. Please try again.');
            }

            setPreferencesSuccess('');
        }
    };

    const handleLogout = async () => {
        try {
            // Optional: call backend to clear the cookie
            await axios.post('/api/login/logout', {}, { withCredentials: true });
        } catch (err) {
            console.error('Logout error:', err);
        }

        // Clear frontend data
        localStorage.clear();
        localStorage.removeItem('userRole');
        localStorage.removeItem('firstName');
        localStorage.removeItem('userId');
        localStorage.removeItem('clientId'); // if applicable
        sessionStorage.clear();

        // Redirect to login
        window.location.href = '/login';
    };

    return (
        <div className="client-settings-container">
            <div className="client-settings-header">
                <h2 className="cb__title">Settings</h2>
                <button
                    onClick={handleLogout}
                    className="client-settings-logout-button"
                >
                    Logout
                </button>
            </div>

            <div className="client-settings-sections">

                {/* Contact details + WhatsApp notifications */}
                <section className="client-settings-card">
                    <h2 className="cb__title">Contact Details</h2>
                    <p className="client-settings-card-subtitle">
                        Update the phone number we use to reach you and manage your WhatsApp notifications.
                    </p>

                    <form onSubmit={handleSaveContactSettings} className="client-settings-form">
                        <div className="client-settings-form-group">
                            <label className="client-settings-label">Phone Number</label>
                            <PhoneInput
                                defaultCountry="ae"
                                value={phoneNumber}
                                onChange={(value) => {
                                    setPhoneNumber(value);
                                    if (preferencesError) setPreferencesError('');
                                    if (preferencesSuccess) setPreferencesSuccess('');
                                }}
                                placeholder="Enter phone number"
                                className="client-settings-phone-wrapper"
                                inputClassName="client-settings-phone-input"
                            />
                        </div>

                        <div className="client-settings-form-group client-settings-toggle-row">
                            <div>
                                <label className="client-settings-label">WhatsApp Notifications</label>
                                <p className="client-settings-helper">
                                    Receive booking updates and important alerts via WhatsApp.
                                    Payment reminders and important service updates are always
                                    sent, even when notifications are turned off.
                                </p>
                            </div>
                            <label className="client-settings-switch">
                                <input
                                    type="checkbox"
                                    checked={isWhatsappNotifications}
                                    onChange={(e) => setIsWhatsappNotifications(e.target.checked)}
                                />
                                <span className="client-settings-slider" />
                            </label>
                        </div>

                        <button type="submit" className="client-settings-primary-button">
                            Save Contact Settings
                        </button>

                        {preferencesError && (
                            <p className="client-settings-error">{preferencesError}</p>
                        )}
                        {preferencesSuccess && (
                            <p className="client-settings-success">{preferencesSuccess}</p>
                        )}
                    </form>
                </section>

                {/* Password section */}
                <section className="client-settings-card">
                    <h2 className="cb__title">Change Password</h2>
                    <p className="client-settings-card-subtitle">
                        Keep your account secure with a strong, unique password.
                    </p>

                    <form onSubmit={handleChangePassword} className="client-settings-form">
                        <div className="client-settings-form-group">
                            <label className="client-settings-label">Current Password</label>
                            <input
                                type="password"
                                placeholder="Enter current password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                                className="client-settings-input"
                            />
                        </div>

                        <div className="client-settings-form-group">
                            <label className="client-settings-label">New Password</label>
                            <input
                                type="password"
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                className="client-settings-input"
                            />
                        </div>

                        <div className="client-settings-form-group">
                            <label className="client-settings-label">Confirm New Password</label>
                            <input
                                type="password"
                                placeholder="Confirm new password"
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                required
                                className="client-settings-input"
                            />
                        </div>

                        <button type="submit" className="client-settings-primary-button">
                            Change Password
                        </button>

                        {passwordError && (
                            <p className="client-settings-error">{passwordError}</p>
                        )}
                        {passwordSuccess && (
                            <p className="client-settings-success">{passwordSuccess}</p>
                        )}
                    </form>
                </section>

                {/* Contact us (2026-08-04) — the office, one tap away. */}
                <section className="client-settings-card">
                    <h3 className="client-settings-card-title">Contact us</h3>
                    <div className="client-settings-contactRow">
                        <a
                            className="client-settings-contactBtn client-settings-contactBtn--wa"
                            href="https://wa.me/971585891289?text=Hi!%20I%20need%20help%20with%20my%20account."
                            target="_blank" rel="noreferrer"
                        >
                            WhatsApp · +971 58 589 1289
                        </a>
                        <a
                            className="client-settings-contactBtn"
                            href="mailto:hello@holidayhomecleaning.ae"
                        >
                            hello@holidayhomecleaning.ae
                        </a>
                        <a
                            className="client-settings-contactBtn"
                            href="https://holidayhomecleaning.ae"
                            target="_blank" rel="noreferrer"
                        >
                            holidayhomecleaning.ae
                        </a>
                    </div>
                </section>

                {/* Danger zone (2026-08-04): deletion is a REQUEST — the
                    office settles bookings/balance/invoices before deactivating,
                    so this notifies staff rather than deleting anything. */}
                <section className="client-settings-card client-settings-card--danger">
                    <h3 className="client-settings-card-title">Delete account</h3>
                    <p className="client-settings-danger-text">
                        Want to close your account? Send us a deletion request — our team
                        will review any open bookings, balance and invoices, then contact
                        you on WhatsApp to confirm.
                    </p>
                    {deletionMsg ? (
                        <div className="client-settings-danger-ok">{deletionMsg}</div>
                    ) : (
                        <button
                            type="button"
                            className="client-settings-danger-button"
                            disabled={deletionBusy}
                            onClick={async () => {
                                if (!window.confirm('Request deletion of your account? Our team will contact you to confirm.')) return;
                                setDeletionBusy(true);
                                try {
                                    const res = await axios.post('/api/client-requests/settings/request-deletion', {}, { withCredentials: true });
                                    setDeletionMsg(res.data?.message || 'Request sent.');
                                } catch (e) {
                                    setDeletionMsg('');
                                    alert(e.response?.data?.error || 'Could not send the request — please try again.');
                                } finally {
                                    setDeletionBusy(false);
                                }
                            }}
                        >
                            {deletionBusy ? 'Sending…' : 'Request account deletion'}
                        </button>
                    )}
                </section>
            </div>
        </div>
    );
}

export default ClientSettings;